import { ChangeEvent, FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Options, RRule } from 'rrule';

import CustomFormInput from '~src/shared/form/inputs/FormInput';
import PrimaryButton from '~src/shared/buttons/basicButtons/PrimaryButton.sc';
import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import WeekdayBubbleSelector from '~src/shared/form/weekdayBubbles/WeekdayBubbleSelector';
import { ButtonSection } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import SelectInput from '~src/shared/form/select/SelectInput';
import {
  hourTimeFactory,
  monthDaysNums,
  remindPeriods,
  ruleWeekdayMap,
  times,
  weekdayArr,
  weekdays,
} from '~src/modules/okrs/constants/options';

import {
  FieldWrapper,
  FormContainer,
  FormLine,
  FormLineTitle,
  FormSubTitle,
  FormTitle,
} from './repeatFormModal.sc';

interface Props {
  value?: string;
  onSubmit: (val: string) => void;
  hide: () => void;
}

const RepeatFormModal: FC<Props> = props => {
  const [t] = useTranslation();
  const selectorTimes = useMemo(
    () =>
      times.map(selectorTime => ({
        name: moment.utc(selectorTime, 'HH:mm').format('HH:mm'),
        value: selectorTime,
      })),
    [],
  );

  const periods = useMemo(
    () =>
      Object.entries(remindPeriods).map(([key, text]) => ({
        name: t(`x.okr.objective.form.remind.periods.${text}`),
        value: key,
      })),
    [],
  );

  const monthDays = useMemo(
    () =>
      monthDaysNums.map((day: number) => ({
        name: t('x.okr.objective.form.remind.month.day', { day: day.toString() }),
        value: day.toString(),
      })),
    [],
  );

  const options = props.value ? RRule.fromString(props.value).options : null;

  const [amount, setAmount] = useState<null | number>(options ? options.interval : 1);
  const [time, setTime] = useState(hourTimeFactory(options ? options.byhour[0] : 0));
  const [period, setPeriod] = useState<string>(
    options && options.bymonthday && options.bymonthday.length > 0
      ? RRule.MONTHLY.toString()
      : options && options.byweekday && options.byweekday.length > 0
      ? RRule.WEEKLY.toString()
      : RRule.DAILY.toString(),
  );
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>(
    options && options.byweekday ? options.byweekday.map((num: number) => weekdayArr[num]) : [],
  );

  const [selectedMonthDay, setSelectedMonthDay] = useState<string>(
    options && options.bymonthday.length > 0 ? options.bymonthday[0].toString() : '1',
  );

  const changeWeekday = (days: string[]) => {
    days = days.reduce(
      (unique: string[], day: string) => (unique.includes(day) ? unique : [...unique, day]),
      [],
    );
    setSelectedWeekdays(days);
  };

  const changeAmount = (e: ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value ? parseInt(e.target.value, 10) : null);
  };

  const changeTime = (selectedTime: string) => {
    setTime(selectedTime);
  };

  const changePeriod = (val: string) => {
    setPeriod(val);
    // reset the values of the other periods
    setSelectedWeekdays([]);
    setSelectedMonthDay('1');
  };

  const changeMonthDay = (val: string) => {
    setSelectedMonthDay(val);
  };

  const submit = () => {
    const ruleObj: Partial<Options> = {
      freq: parseInt(period, 10),
      interval: amount || 1,
      byhour: parseInt(time.split(':')[0], 10),
      byminute: 0,
      bysecond: 0,
    };

    if (period === RRule.WEEKLY.toString()) {
      ruleObj.byweekday = selectedWeekdays.map(weekday => ruleWeekdayMap[weekday]);
    }
    if (period === RRule.MONTHLY.toString()) {
      ruleObj.bymonthday = [+selectedMonthDay];
    }
    const rule = new RRule(ruleObj);
    props.onSubmit(rule.toString());
  };

  const periodSection = () => {
    switch (period) {
      case RRule.DAILY.toString(): {
        return null;
      }
      case RRule.WEEKLY.toString(): {
        return (
          <WeekdayBubbleSelector defaultVal={selectedWeekdays} weekdays={weekdays} onChange={changeWeekday} />
        );
      }
      case RRule.MONTHLY.toString(): {
        return (
          <SelectInput
            t={t}
            value={selectedMonthDay}
            label={t('x.okr.objective.repetition.remind.section.monthly')}
            optList={monthDays}
            labelKey={'x.okr.objective.repetition.remind.section.monthly'}
            onChange={changeMonthDay}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <FormContainer>
      <FormTitle data-translation="x.okr.objective.repetition.title">
        {t('x.okr.objective.repetition.title')}
      </FormTitle>
      <FormSubTitle data-translation="x.okr.objective.repetition.subtitle">
        {t('x.okr.objective.repetition.subtitle')}
      </FormSubTitle>
      <FormLine>
        <FormLineTitle data-translation="x.okr.objective.repetition.remind.section.title">
          {t('x.okr.objective.repetition.remind.section.title')}
        </FormLineTitle>
        <FieldWrapper>
          <CustomFormInput
            min={1}
            type="number"
            data-translation="x.okr.objective.repetition.remind.section.amount"
            value={amount || ''}
            onChange={changeAmount}
            label={t('x.okr.objective.repetition.remind.section.amount')}
            labelKey="x.okr.objective.repetition.remind.section.amount"
          />
        </FieldWrapper>
        <FieldWrapper>
          <SelectInput
            t={t}
            value={period}
            label={t('x.okr.objective.repetition.remind.section.period')}
            optList={periods}
            labelKey="x.okr.objective.repetition.remind.section.period"
            onChange={changePeriod}
          />
        </FieldWrapper>
      </FormLine>
      {period !== RRule.DAILY.toString() && (
        <FormLine>
          <FormLineTitle
            data-translation={`x.okr.objective.repetition.remind.section.${remindPeriods[period]}.title`}
          >
            {t(`x.okr.objective.repetition.remind.section.${remindPeriods[period]}.title`)}
          </FormLineTitle>
          <FieldWrapper>{periodSection()}</FieldWrapper>
        </FormLine>
      )}
      <FormLine>
        <FormLineTitle data-translation="x.okr.objective.repetition.remind.time.section.title ">
          {t('x.okr.objective.repetition.remind.time.section.title')}
        </FormLineTitle>
        <FieldWrapper>
          <SelectInput
            t={t}
            label={t('x.okr.objective.repetition.remind.section.time')}
            optList={selectorTimes}
            labelKey={'x.okr.objective.repetition.remind.section.time'}
            onChange={changeTime}
            value={time}
          />
        </FieldWrapper>
      </FormLine>
      <ButtonSection>
        <SecondaryButton onClick={props.hide} data-translation="x.okr.labels.button.cancel">
          {t('x.okr.labels.button.cancel')}
        </SecondaryButton>
        <PrimaryButton onClick={submit} data-translation="x.okr.labels.button.done">
          {t('x.okr.labels.button.done')}
        </PrimaryButton>
      </ButtonSection>
    </FormContainer>
  );
};

export default RepeatFormModal;
